import { useMemo } from 'react';
import Link from 'next/link';
import { Typography } from '@/primitives';
import { Card, CardContent, CardMedia } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { slate } from '@/lib/colours';
import { lineClampSx } from '@/utils/text';

export type NewsCardProps = {
  date?: Date;
  imgUrl?: string;
  isBrief?: boolean;
  referenceLink: string;
  snippet: string;
  title: string;
};

export default function NewsCard({
  date,
  imgUrl,
  isBrief = false,
  referenceLink,
  snippet,
  title,
}: NewsCardProps) {
  const timeAgo = date ? formatDistanceToNow(date, { addSuffix: true }) : '';
  const formattedTime = useMemo(
    () => timeAgo.replace(/^about |^over |^less than |^almost /, ''),
    [timeAgo],
  );

  return (
    <Link href={referenceLink} style={{ textDecoration: 'none' }}>
      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: isBrief ? 'row' : 'column',
          borderRadius: 0,
          maxWidth: { xs: 'unset', md: '378px' },
          backgroundColor: 'transparent',
        }}
      >
        <CardMedia
          alt="news image"
          component="img"
          image={imgUrl}
          sx={{
            borderRadius: isBrief ? 0 : '12px',
            height: {
              sm: isBrief ? '96px' : 'unset',
            },
            width: {
              sm: isBrief ? '96px' : '100%',
            },
            aspectRatio: isBrief
              ? '1 / 1'
              : {
                  xs: '350 / 300',
                  md: '336 / 260',
                },
          }}
        />
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: isBrief ? '0 16px !important' : '12px 0 !important',
          }}
        >
          <Typography
            color={slate[900]}
            variant={isBrief ? 'titlesmall' : 'titlemedium'}
          >
            {title}
          </Typography>
          <Typography
            color={slate[600]}
            sx={{
              ...lineClampSx,
              mt: isBrief ? 0 : '4px',
              lineHeight: isBrief ? '24px' : '20px',
            }}
            variant="bodymedium"
          >
            {snippet}
          </Typography>
          <Typography
            color={slate[500]}
            sx={{ mt: isBrief ? 0 : '8px' }}
            variant="bodymedium"
          >
            {formattedTime}
          </Typography>
        </CardContent>
      </Card>
    </Link>
  );
}
